import { computed, reactive, toRefs } from '@vue/composition-api';
import useHttpClient from '@/compositions/useHttpClient';
import {
  ERRORS_ADD_TO_WISH_LIST,
  ERRORS_REMOVE_IN_WISH_LIST,
} from '@/utils/constants/wishListConstants';

const useWishListCart = () => {
  const { wishList } = useHttpClient();

  const localState = reactive({
    wishList: [],
    pagination: {
      total: 0,
      count: 0,
      perPage: 10,
      totalPages: 1,
      currentPage: 1,
    },
    removedProductsCount: 0,
    isEmptyWishList: false,
  });

  /**
   * Если в серии только одни товар или серия на модерации
   * то удаляем ссылку
   * @return {Array}
   */
  const checkSeries = (list) => {
    if (list.length) {
      list.forEach((product) => {
        if (product.series) {
          product.series.forEach((serie) => {
            if (serie.numBooks <= 1 || !serie.code) serie.link = null;
          });
        }
      });
    }
  };

  /**
   * Получает список отложенных товаров
   * @return {Promise<unknown>}
   */
  const getList = async (options, replace) => new Promise((resolve, reject) => {
    wishList.getWishList({ ...options })
      .then(({ list, pagination }) => {
        const filteredList = list.filter(product => product);
        checkSeries(filteredList);
        localState.isEmptyWishList = !list.length;
        if (replace) {
          localState.wishList = filteredList;
        } else {
          localState.wishList = [...localState.wishList, ...filteredList];
        }
        localState.pagination = pagination;
        resolve({ list, pagination });
      })
      .catch((error) => {
        reject(error);
      });
  });

  /**
   * Удаляет отложенный товар
   * @param {number | string} productId
   * @return {Promise<unknown>}
   */
  const removeProduct = async (productId) => {
    await wishList.removeInWishListItem(productId)
      .catch((error) => {
        if (!ERRORS_REMOVE_IN_WISH_LIST.getItemByCode(error?.code)) throw error;
      });
  };

  /**
   * Добавляет товар в отложенные
   * @param {number | string} productId
   * @return {Promise<unknown>}
   */
  const addToWishList = async (productId) => {
    await wishList.addToWishList(productId)
      .catch((error) => {
        if (!ERRORS_ADD_TO_WISH_LIST.getItemByCode(error?.code)) throw error;
      });
  };

  const isMayLoadMore = computed(() => {
    const totalPages = localState.pagination.totalPages;
    const currentPage = localState.pagination.currentPage;
    return currentPage < totalPages;
  });

  const loadMoreCount = computed(() => {
    const currentPage = localState.pagination.currentPage;
    const totalProducts = localState.pagination.total;
    const countPerPage = localState.pagination.count;

    const passedProductsCount = currentPage * countPerPage;
    const countIsMayLoaded = totalProducts - passedProductsCount;

    if (countIsMayLoaded >= countPerPage) {
      return countPerPage;
    }

    return countIsMayLoaded;
  });

  const totalProducts = computed(() => localState.pagination.total - localState.removedProductsCount);

  const sliceProductFromList = (product) => {
    const productIndex = localState.wishList.findIndex(item => item.id === product.id);
    if (productIndex > -1) {
      localState.wishList.splice(productIndex, 1);
      if (product.kitProduct) {
        let kitProductIndex = localState.wishList.findIndex(
          item => item.kitProduct?.kitCode === product.kitProduct.kitCode,
        );
        while (kitProductIndex > -1) {
          localState.wishList.splice(kitProductIndex, 1);
          kitProductIndex = localState.wishList.findIndex(
            item => item.kitProduct?.kitCode === product.kitProduct.kitCode,
          );
        }
      }
    }
  };

  const updateRemovedCount = (isRemoved) => {
    if (isRemoved) {
      localState.removedProductsCount += 1;
    } else {
      localState.removedProductsCount -= 1;
    }
  };

  const editKitProduct = ({ kitCode, isRemoved, productCardRefs }) => {
    if (productCardRefs) {
      localState.wishList.forEach((item, key) => {
        if (productCardRefs[key]) {
          const productCard = productCardRefs[key];
          if (item.kitProduct && item.kitProduct?.kitCode === kitCode) {
            productCard.removed = isRemoved;
            updateRemovedCount(isRemoved);
          }
        }
      });
    }
  };

  return {
    ...toRefs(localState),
    getList,
    removeProduct,
    addToWishList,
    loadMoreCount,
    isMayLoadMore,
    sliceProductFromList,
    totalProducts,
    editKitProduct,
    checkSeries,
    updateRemovedCount,
  };
};

export default useWishListCart;
